import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Car, PawPrint, Users, CalendarCheck, HandHeart, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";
import { WhereWeDrive } from "@/components/WhereWeDrive";
import { useLang } from "@/context/LanguageContext";

const SERVICES = [
  {
    icon: Compass,
    title: "Relocation Guidance",
    title_es: "Orientación para tu mudanza",
    text: "Residency paperwork, timelines, budgets, and a written roadmap for your move — explained in plain English.",
    text_es: "Trámites de residencia, tiempos, presupuestos y una hoja de ruta por escrito para tu mudanza — explicado con claridad.",
    to: "/how-we-help",
    cta: "How we help",
    cta_es: "Cómo ayudamos",
    links: [
      { to: "/start-here", label: "Start Here quiz", label_es: "Quiz Empieza aquí" },
      { to: "/moving-checklist", label: "Moving checklist", label_es: "Lista de mudanza" },
      { to: "/residency-documents", label: "Residency & documents", label_es: "Residencia y documentos" },
    ],
  },
  {
    icon: Car,
    title: "Driving & Transport",
    title_es: "Manejo y transporte",
    text: "Door-to-door drivers, border crossing support, and route planning from the US to your new home in Mexico.",
    text_es: "Choferes puerta a puerta, apoyo en el cruce fronterizo y planeación de ruta de EE. UU. a tu nuevo hogar en México.",
    to: "/drivers-transport",
    cta: "Drivers & transport",
    cta_es: "Choferes y transporte",
    links: [
      { to: "/drivers", label: "Meet our drivers", label_es: "Conoce a nuestros choferes" },
      { to: "/driving", label: "Driving to Mexico guide", label_es: "Guía para manejar a México" },
      { to: "/fmm-land-entry", label: "FMM / land entry", label_es: "FMM / entrada por tierra" },
    ],
  },
  {
    icon: PawPrint,
    title: "Pet Transport",
    title_es: "Transporte de mascotas",
    text: "Vet paperwork, travel crates, and calm, pet-friendly routes. Dogs and cats ride like family — no quarantine.",
    text_es: "Papeleo veterinario, transportadoras y rutas tranquilas. Perros y gatos viajan como familia — sin cuarentena.",
    to: "/pet-transport",
    cta: "Pet transport",
    cta_es: "Transporte de mascotas",
    links: [
      { to: "/pets", label: "Moving with pets guide", label_es: "Guía para mudarte con mascotas" },
      { to: "/pet-transport", label: "Pet transport service", label_es: "Servicio de transporte de mascotas" },
    ],
  },
];

const GUIDES = [
  { to: "/merida", label: "Mérida (home base)" },
  { to: "/explore-mexico", label: "Explore Mexico" },
  { to: "/your-route", label: "Your Route" },
  { to: "/mexico-city", label: "Mexico City" },
  { to: "/guadalajara", label: "Guadalajara" },
  { to: "/queretaro", label: "Querétaro" },
  { to: "/puerto-vallarta", label: "Puerto Vallarta" },
  { to: "/san-miguel-de-allende", label: "San Miguel de Allende" },
  { to: "/oaxaca-city", label: "Oaxaca City" },
  { to: "/monterrey", label: "Monterrey" },
  { to: "/cities-housing", label: "Cities & Housing" },
  { to: "/residency-documents", label: "Residency & Documents" },
  { to: "/fmm-land-entry", label: "FMM / Land Entry" },
  { to: "/driving", label: "Driving to Mexico" },
  { to: "/life-in-mexico", label: "Life in Mexico" },
  { to: "/moving-checklist", label: "Moving Checklist" },
  { to: "/cost-planning", label: "Cost & Planning" },
  { to: "/safety-travel-planning", label: "Safety & Travel Planning" },
  { to: "/arrival-settling", label: "Arrival & Settling In" },
  { to: "/how-we-help", label: "How We Help" },
  { to: "/blog", label: "Resources & Blog" },
  { to: "/updates", label: "Updates" },
];

export default function Services() {
  const { lang } = useLang();
  const tr = (en, es) => (lang === "es" && es ? es : en);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="page-services">
      <Seo
        path="/services"
        title="Services — Relocation Guidance, Driving & Pet Transport"
        description="Everything Hola Movers does: relocation guidance, door-to-door driving & transport, and pet transport from the US to Mexico."
      />
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pt-16 lg:pt-24">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900">
          {lang === "es" ? "Nuestros servicios" : "Our services"}
        </h1>
        <p className="mt-5 text-lg text-charcoal-600 max-w-2xl leading-relaxed">
          {lang === "es"
            ? "Tres formas de ayudarte a mudarte de EE. UU. a México — con calma y claridad."
            : "Three ways we help you move from the US to Mexico — calmly and clearly."}
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-12 lg:py-16">
        <div className="grid gap-6 md:grid-cols-3">
          {SERVICES.map((s) => (
            <div
              key={s.title}
              data-testid={`service-card-${s.to.slice(1)}`}
              className="flex flex-col rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)]"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta-light text-terracotta mb-6">
                <s.icon strokeWidth={1.5} />
              </span>
              <h2 className="text-xl font-medium text-charcoal-800">{tr(s.title, s.title_es)}</h2>
              <p className="mt-2 text-charcoal-600 leading-relaxed">{tr(s.text, s.text_es)}</p>
              <ul className="mt-5 space-y-2 flex-1">
                {s.links.map((l) => (
                  <li key={l.to + l.label}>
                    <Link to={l.to} className="text-sm text-charcoal-600 hover:text-terracotta transition-colors underline underline-offset-4 decoration-sand-300">
                      {tr(l.label, l.label_es)}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to={s.to}
                data-testid={`service-cta-${s.to.slice(1)}`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-terracotta group"
              >
                {tr(s.cta, s.cta_es)}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Users, label: "Meet our drivers", label_es: "Conoce a nuestros choferes", to: "/drivers" },
            { icon: CalendarCheck, label: "Book an intro call", label_es: "Agenda una llamada", to: "/book" },
            { icon: HandHeart, label: "Get a quote", label_es: "Cotiza ahora", to: "/cost-calculator" },
          ].map((q) => (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`service-quicklink-${q.to.slice(1).replace("/", "-")}`}
              className="flex items-center gap-3 rounded-2xl bg-sand-200 border border-sand-300 px-5 py-4 font-medium text-charcoal-800 hover:bg-sand-300 hover:text-terracotta transition-colors"
            >
              <q.icon size={18} strokeWidth={1.5} className="text-terracotta" /> {tr(q.label, q.label_es)}
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-6">
        <WhereWeDrive />
      </section>

      <section id="guides" className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20" data-testid="services-guides">
        <div className="flex items-center gap-2 text-clay-700 mb-4">
          <BookOpen size={18} strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-[0.2em]">{lang === "es" ? "Guías gratuitas" : "Free guides"}</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800">
          {lang === "es" ? "Todas nuestras guías" : "All our guides"}
        </h2>
        <p className="mt-3 text-charcoal-600 max-w-2xl">
          {lang === "es"
            ? "Todo lo que hemos escrito sobre mudarse a México — gratis y sin registro."
            : "Everything we've written about moving to Mexico — free, no sign-up needed."}
        </p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {GUIDES.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              data-testid={`guide-link-${g.to.slice(1).replace(/\//g, "-")}`}
              className="group flex items-center justify-between rounded-2xl bg-white border border-sand-200 px-5 py-3.5 text-sm font-medium text-charcoal-700 hover:border-terracotta/40 hover:text-terracotta transition-colors"
            >
              {g.label}
              <ArrowRight size={14} className="text-sand-400 transition-all group-hover:text-terracotta group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
